/**
 * Readable presentation of engine finding text.
 *
 * The engine (qoe_model.rs) writes findings as terse analyst notes keyed by internal identifiers:
 * `Northline Home Services/2025-12/1020`, adjustment UUIDs, `42000 USD`. The server passes that text
 * through unchanged, which is correct for the workpapers. This module rewrites the WORDING only for
 * the guided front door: account and adjustment names replace identifiers, months are spelled out
 * and amounts are formatted. Codes, severities, counts and every number stay exactly as the engine
 * produced them — nothing here can change what a finding says, only how it reads.
 *
 * Promoted from the offline demo (scripts/qoe/demo/app/presentFindings.ts), which now re-exports it
 * so there is a single implementation.
 */
import { formatQoeMoney, type QoeSourceAccount } from '../../../../../data/qoeApi'
import type { QoeAnalysis, QoeInput } from '../../../../../data/qoeTypes'

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
const escape = (v: string) => v.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
export const monthName = (period: string) => {
  const m = /^(\d{4})-(\d{2})$/.exec(period)
  return m && Number(m[2]) >= 1 && Number(m[2]) <= 12 ? `${MONTHS[Number(m[2]) - 1]} ${m[1]}` : period
}

export interface PresentationContext {
  input: QoeInput
  analysis: QoeAnalysis
  accounts: QoeSourceAccount[]
}

export function createQoeFindingPresenter({ input, analysis, accounts }: PresentationContext) {
  const entities = input.settings?.entities || []
  const scoped = entities.filter(e => e.in_perimeter && !e.is_elimination)
  const entityName = (id: string) => entities.find(e => e.id === id)?.name || id
  const accountLabel = (entity: string, code: string) => {
    const name = accounts.find(a => a.entity_id === entity && a.account_code === code)?.account_name
    return name ? `${name.replace(new RegExp(`^${escape(code)}\\s*[·:-]\\s*`), '')} (account ${code})` : `account ${code}`
  }
  const where = (entity: string, period: string, code?: string) => {
    const parts = [code ? accountLabel(entity, code) : '', monthName(period)].filter(Boolean)
    if (scoped.length > 1 || !entities.some(e => e.id === entity)) parts.unshift(entityName(entity))
    return parts.join(', ')
  }
  const unprovenAccounts = (entity: string, period: string) =>
    [...new Set((analysis.cash || []).filter(c => c.entity_id === entity && c.period === period && c.status === 'unavailable').map(c => accountLabel(c.entity_id, c.account_code)))]
  const bankMissing = (entity: string, period: string, code: string) =>
    !(input.source?.bank || []).some(b => b.entity_id === entity && b.period === period && b.account_code === code)
  const titles = new Map((input.adjustments || []).map(a => [a.id, a.title]))
  const sources = new Map((input.source?.manifests || []).map(m => [m.id, m.filename]))
  const entityPattern = entities.length ? entities.map(e => escape(e.id)).sort((a, b) => b.length - a.length).join('|') : '(?!)'
  const listed = (names: string[]) => names.length <= 1 ? names.join('') : `${names.slice(0, -1).join(', ')} and ${names.at(-1)}`

  const rules: Array<[RegExp, (...groups: string[]) => string]> = [
    [new RegExp(`^Four-way cash proof unavailable for (${entityPattern})/(\\d{4}-\\d{2})/([^:]+): independent bank activity and book opening/closing are required$`), (entity, period, code) =>
      bankMissing(entity, period, code)
        ? `Four-way proof of cash unavailable for ${where(entity, period, code)}: the bank statement has not been received, so book activity is shown but not independently proven.`
        : `Four-way proof of cash unavailable for ${where(entity, period, code)}: independent bank activity and book opening and closing balances are required.`],
    [new RegExp(`^Independent cash proof and classified receipt activity are required for (${entityPattern})/(\\d{4}-\\d{2})$`), (entity, period) => {
      const names = unprovenAccounts(entity, period)
      return names.length
        ? `Customer receipts to revenue unavailable for ${where(entity, period)}: receipts cannot be proven until bank evidence is received for the ${listed(names)}.`
        : `Customer receipts to revenue unavailable for ${where(entity, period)}: independent cash proof and classified receipt activity are required.`
    }],
    [/^Cash conversion for (\d{4}-\d{2}) requires complete opening NWC, classified book activity and independent bank proof$/, period => {
      const nwcComplete = (analysis.nwc?.periods || []).some(p => p.period === period && p.complete)
      const names = [...new Set(scoped.flatMap(e => unprovenAccounts(e.id, period)))]
      return nwcComplete && names.length
        ? `EBITDA to operating cash conversion unavailable for ${monthName(period)}: operating cash cannot be proven until bank evidence is received for the ${listed(names)}.`
        : `EBITDA to operating cash conversion unavailable for ${monthName(period)}: complete opening NWC, classified book activity and independent bank proof are required.`
    }],
    [new RegExp(`(${entityPattern})/(\\d{4}-\\d{2})/([A-Za-z0-9._-]+)`, 'g'), (entity, period, code) => where(entity, period, code)],
    [new RegExp(`(${entityPattern})/(\\d{4}-\\d{2})(?![\\w/-])`, 'g'), (entity, period) => where(entity, period)],
  ]

  const presentText = (text: string): string => {
    let out = String(text ?? '')
    for (const [pattern, render] of rules) {
      if (pattern.global) out = out.replace(pattern, (_match, ...groups) => render(...groups.slice(0, -2).map(String)))
      else { const m = pattern.exec(out); if (m) out = render(...m.slice(1)) }
    }
    for (const [id, title] of titles) if (id && title && out.includes(id)) out = out.split(id).join(`“${title}”`)
    out = out
      .replace(/(-?\d+(?:\.\d+)?) USD\b/g, (_m, amount) => formatQoeMoney(amount))
      .replace(/\b(difference|exposure) (-?\d+(?:\.\d+)?)(?![\w.-])/g, (_m, word, amount) => `${word} ${formatQoeMoney(amount)}`)
      .replace(/(?<![\w-])(\d{4}-\d{2})(?![\w-])/g, period => monthName(period))
    return out
  }
  const presentReference = (id: string) => titles.get(id) || sources.get(id) || id
  return { presentText, presentReference }
}

/** Returns a copy of the analysis with readable finding text; codes, severities and amounts are untouched. */
export function presentQoeAnalysis(input: QoeInput, analysis: QoeAnalysis, accounts: QoeSourceAccount[]): QoeAnalysis {
  if (!analysis?.findings?.length) return analysis
  const { presentText, presentReference } = createQoeFindingPresenter({ input, analysis, accounts })
  return { ...analysis, findings: analysis.findings.map(f => ({ ...f, message: presentText(f.message), reference_ids: (f.reference_ids || []).map(presentReference) })) }
}
